import React, { ReactElement, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import CountrySelector from './CountrySelector'
import { simpleEmailValidation } from '../util/Util'
import { RADOM_COLORS } from '../util/Constants'
import { PrimaryButton } from './Button'
import {
  emptyTravelRuleFields,
  TravelRuleFields,
  TravelRuleFieldsCollected,
  CheckoutType
} from './Checkout'
import {
  TextInputWithLabelWithSuggestions,
  InputLabel,
  TextInput,
  InlineInputError,
  INPUT_ERROR_STYLE
} from './Input'
import Switch from './Switch'
import Radom, { type AddressSuggestionDetails, type Profile } from '../api/Radom'
import _ from 'lodash'
import { Country, State } from 'country-state-city'
import Cookies from 'js-cookie'
const COOKIE_KEY = 'radom_travel_rule_fields'
const ADDRESS_MODE_DELAY_MS = 120
const COUNTRIES = Country.getAllCountries()
const COUNTRY_ALIAS_MAP: Record<string, string> = {
  UK: 'United Kingdom',
  'U K': 'United Kingdom',
  GB: 'United Kingdom',
  GBR: 'United Kingdom',
  'GREAT BRITAIN': 'United Kingdom',
  'UNITED KINGDOM OF GREAT BRITAIN AND NORTHERN IRELAND': 'United Kingdom',
  USA: 'United States',
  US: 'United States',
  'U S': 'United States',
  'UNITED STATES OF AMERICA': 'United States',
  UAE: 'United Arab Emirates'
}

const countryLookupKey = (country: string): string => {
  return country
    .trim()
    .toUpperCase()
    .replace(/[().]/g, '')
    .replace(/,/g, ' ')
    .replace(/\s+/g, ' ')
}

const normalizeCountryName = (country?: string): string | undefined => {
  if (!country) {
    return undefined
  }

  const trimmed = country.trim()
  if (trimmed.length === 0) {
    return undefined
  }

  const lookupKey = countryLookupKey(trimmed)
  const aliasMatch = COUNTRY_ALIAS_MAP[lookupKey]
  if (aliasMatch) {
    return aliasMatch
  }

  const isoMatch = COUNTRIES.find(c => countryLookupKey(c.isoCode) === lookupKey)
  if (isoMatch) {
    return isoMatch.name
  }

  const exactMatch = COUNTRIES.find(c => countryLookupKey(c.name) === lookupKey)
  if (exactMatch) {
    return exactMatch.name
  }

  return trimmed
}

const getCountryIsoCode = (country?: string): string => {
  const normalized = normalizeCountryName(country)
  if (!normalized) {
    return ''
  }

  return COUNTRIES.find(c => c.name === normalized)?.isoCode ?? ''
}

const firstNonEmpty = (...values: Array<string | undefined>): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim()
    }
  }
  return undefined
}

const parseFallbackAddress = (address: string): Partial<TravelRuleFields> => {
  const parts = address
    .split(',')
    .map(part => part.trim())
    .filter(part => part.length > 0)

  if (parts.length === 0) {
    return {}
  }

  const addressLine1 = parts[0]
  const countryRaw = parts.length > 1 ? parts[parts.length - 1] : ''
  const townOrCityRaw = parts.length > 2 ? parts[parts.length - 2] : parts[1]
  let townOrCity = townOrCityRaw ?? ''
  let stateOrProvince = ''
  let postalCode = ''

  const usLikeMatch = townOrCity.match(/^(.+?)(?:,\s*|\s+)([A-Za-z]{2})\s+([A-Za-z0-9- ]{3,10})$/)
  if (usLikeMatch) {
    townOrCity = usLikeMatch[1]
    stateOrProvince = usLikeMatch[2]
    postalCode = usLikeMatch[3]
  } else {
    const postCodeAtEnd = townOrCity.match(/^(.+?)\s+([A-Za-z0-9-]{3,10})$/)
    if (postCodeAtEnd && /\d/.test(postCodeAtEnd[2])) {
      townOrCity = postCodeAtEnd[1]
      postalCode = postCodeAtEnd[2]
    }
  }

  return {
    addressLine1,
    townOrCity: townOrCity?.trim() ?? '',
    stateOrProvince: stateOrProvince?.trim() ?? '',
    postalCode: postalCode?.trim() ?? '',
    countryOfResidence: normalizeCountryName(countryRaw)
  }
}

// ---------------------------------------------------------------------------------------------------------------------

const TravelRuleFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0;
  font-size: 15px;
  width: min(640px, 100%);
  max-width: 640px;
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: 900px) {
    width: 100%;
    max-width: 100%;
  }
`

const TravelRuleFieldsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  min-width: 0;
`

const EmailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  min-width: 0;
`

const BusinessToggleRow = styled.div`
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  margin-top: 20px;
`

const IdentitySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
  width: 100%;
  min-width: 0;
`

const BillingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 26px;
  width: 100%;
  min-width: 0;
`

const BillingSectionTitle = styled.div`
  font-size: 13px;
  font-weight: 430;
  color: #7a879d;
  letter-spacing: 0.01em;
  text-transform: none;
  margin-bottom: 4px;
`

const AddressManualLink = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 4px 2px 4px 10px;
  min-height: 24px;
  margin: 0;
  width: fit-content;
  font-size: 13px;
  line-height: 1.2;
  color: #97a3b6;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  font-weight: 400;
  transition:
    opacity 0.16s ease,
    color 0.16s ease;

  &:hover {
    color: #7f8ba0;
  }

  &:focus-visible {
    outline: none;
    color: #7f8ba0;
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`

const AddressModeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-height: 24px;
  margin-top: 8px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`

const AddressFieldsGroup = styled.div<{ visible: boolean, expandedHeight: number }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  max-height: ${({ visible, expandedHeight }) => visible ? `${expandedHeight}px` : '0px'};
  opacity: ${({ visible }) => visible ? 1 : 0};
  transform: translateY(${({ visible }) => visible ? '0' : '-6px'});
  visibility: ${({ visible }) => visible ? 'visible' : 'hidden'};
  pointer-events: ${({ visible }) => visible ? 'auto' : 'none'};
  transition:
    max-height 260ms cubic-bezier(0.16, 1, 0.3, 1),
    opacity 220ms ease 40ms,
    transform 220ms ease,
    visibility 0s linear ${({ visible }) => visible ? '0s' : '220ms'};
`

const FieldPair = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const ContinueActionSection = styled.div<{ extraBottomSpace?: boolean }>`
  margin-top: 28px;
  padding-top: 12px;
  padding-bottom: ${({ extraBottomSpace }) =>
    extraBottomSpace
      ? 'calc(88px + env(safe-area-inset-bottom, 0px))'
      : 'calc(20px + env(safe-area-inset-bottom, 0px))'};
  border-top: 1px solid #e2e8f3;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  box-sizing: border-box;
`

const FIELD_INPUT_STYLE: React.CSSProperties = {
  minHeight: 44,
  borderRadius: 6,
  fontSize: 15,
  lineHeight: 1.3,
  padding: '8px 13px',
  borderColor: '#e1e7f0',
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
}

const COUNTRY_SELECTOR_STYLE: React.CSSProperties = {
  minHeight: 44,
  borderRadius: 6,
  fontSize: 15,
  lineHeight: 1.3,
  padding: '7px 13px',
  borderColor: '#e1e7f0',
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
}

// ---------------------------------------------------------------------------------------------------------------------
interface IProps {
  travelRuleFieldsCollected: TravelRuleFieldsCollected
  accountType?: string
  onTravelRuleFieldsComplete: (trFields: TravelRuleFields) => void
  prefilledData?: Partial<Profile>
}
const labelText: Record<string, [string, string]> = {
  name: ['Full name', 'Full name'],
  email: ['Email address', 'Email address'],
  billingAddress: ['Address', 'Address'],
  country: ['Country', 'Country'],
  company: ['', 'Company name']
}
const fetchLabelText = (labelName: string, isBusiness: boolean): string =>
  labelText[labelName][+isBusiness]

function generateInputLabel(
  labelName: string,
  isBusiness: boolean
): ReactElement {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: 1
      }}
    >
      <InputLabel
        style={{
          fontWeight: 400,
          fontSize: 13,
          color: '#76849a'
        }}
      >
        {fetchLabelText(labelName, isBusiness)}
      </InputLabel>
    </div>
  )
}

const getStoredFields = (): TravelRuleFields | null => {
  const stored = Cookies.get(COOKIE_KEY)
  if (stored) {
    try {
      return JSON.parse(stored)
    } catch (e) {
      console.error('Error parsing stored travel rule fields:', e)
    }
  }
  return null
}

const hasTextValue = (value?: string): boolean => {
  return typeof value === 'string' && value.trim().length > 0
}

type ValidationField =
  'emailAddress' |
  'companyName' |
  'name' |
  'countryOfResidence' |
  'address' |
  'addressLine1' |
  'townOrCity'

type ValidationErrors = Partial<Record<ValidationField, string>>
// ---------------------------------------------------------------------------------------------------------------------
const TravelRuleFieldsWidget = (props: IProps): React.ReactElement => {
  const storedFields = getStoredFields()
  const [isBusiness, setIsBusiness] = useState(() => {
    const storedFields = getStoredFields()

    if (storedFields?.accountType) {
      return storedFields.accountType === CheckoutType.Business
    }

    const initialAccountType = props.travelRuleFieldsCollected.accountType ?? props.accountType
    if (initialAccountType) {
      return initialAccountType === 'Business'
    }

    return !!props.prefilledData?.companyName
  })

  const [collectedTravelRuleFields, setCollectedTravelRuleFields] =
    useState<TravelRuleFields>(() => {
      const baseFields = emptyTravelRuleFields()

      if (props.prefilledData) {
        if (props.prefilledData.name) baseFields.name = props.prefilledData.name
        if (props.prefilledData.email) baseFields.emailAddress = props.prefilledData.email
        if (props.prefilledData.companyName) baseFields.companyName = props.prefilledData.companyName
        if (props.prefilledData.residenceCountry) {
          baseFields.countryOfResidence =
            normalizeCountryName(props.prefilledData.residenceCountry) ?? props.prefilledData.residenceCountry
        }

        const prefilledAddress = parseFallbackAddress(props.prefilledData.billingAddress ?? '')
        if (hasTextValue(prefilledAddress.addressLine1)) baseFields.addressLine1 = prefilledAddress.addressLine1
        if (hasTextValue(prefilledAddress.addressLine2)) baseFields.addressLine2 = prefilledAddress.addressLine2
        if (hasTextValue(prefilledAddress.townOrCity)) baseFields.townOrCity = prefilledAddress.townOrCity
        if (hasTextValue(prefilledAddress.stateOrProvince)) {
          baseFields.stateOrProvince = prefilledAddress.stateOrProvince
        }
        if (hasTextValue(prefilledAddress.postalCode)) baseFields.postalCode = prefilledAddress.postalCode
        if (!hasTextValue(baseFields.countryOfResidence) && hasTextValue(prefilledAddress.countryOfResidence)) {
          baseFields.countryOfResidence = prefilledAddress.countryOfResidence
        }
      }

      if (!storedFields) {
        return baseFields
      }

      const merged = { ...baseFields, ...storedFields }
      merged.name = hasTextValue(storedFields.name) ? storedFields.name : baseFields.name
      merged.emailAddress = hasTextValue(storedFields.emailAddress)
        ? storedFields.emailAddress
        : baseFields.emailAddress
      merged.companyName = hasTextValue(storedFields.companyName) ? storedFields.companyName : baseFields.companyName
      merged.countryOfResidence = hasTextValue(storedFields.countryOfResidence)
        ? normalizeCountryName(storedFields.countryOfResidence) ?? storedFields.countryOfResidence
        : baseFields.countryOfResidence

      return merged
    })

  const [errorMessage, setErrorMessage] = useState<string>()
  const [addressSuggestionList, setAddressSuggestionList] = useState<string[]>(
    []
  )
  const [isManualAddressEntry, setIsManualAddressEntry] = useState(false)
  const [pendingAddressMode, setPendingAddressMode] = useState<'manual' | 'autocomplete' | null>(null)
  const [isContinuing, setIsContinuing] = useState(false)
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  const [inputAddress, setInputAddress] = useState<string>(() => props.prefilledData?.billingAddress ?? '')
  const collectedTravelRuleFieldsRef = useRef<TravelRuleFields>(
    collectedTravelRuleFields
  )
  const addressModeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasAttemptedCountryPrefillRef = useRef(false)

  const shouldCollectAddress = true
  const shouldCollectCountry = true
  const selectedCountryName = normalizeCountryName(collectedTravelRuleFields.countryOfResidence)
  const selectedCountryIsoCode = getCountryIsoCode(selectedCountryName)
  const shouldShowStateOrRegionField = selectedCountryIsoCode.length > 0
    ? State.getStatesOfCountry(selectedCountryIsoCode).length > 0
    : false

  const fieldErrorStyle = (field: ValidationField, baseStyle: React.CSSProperties): React.CSSProperties => ({
    ...baseStyle,
    ...(validationErrors[field] ? INPUT_ERROR_STYLE : undefined)
  })

  const clearValidationErrors = (...fields: ValidationField[]): void => {
    setValidationErrors(currentErrors => {
      if (fields.every(field => !currentErrors[field])) {
        return currentErrors
      }

      return Object.fromEntries(
        Object.entries(currentErrors).filter(([field]) => !fields.includes(field as ValidationField))
      ) as ValidationErrors
    })
  }

  const focusValidationField = (field: ValidationField): void => {
    window.requestAnimationFrame(() => {
      const fieldElement = document.querySelector<HTMLElement>(`[data-validation-field="${field}"]`)
      fieldElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      const focusable = fieldElement?.matches('input')
        ? fieldElement
        : fieldElement?.querySelector<HTMLElement>('input, button, [tabindex]:not([tabindex="-1"])')
      focusable?.focus()
    })
  }

  const onCountrySelected = (country: { name: string } | undefined): void => {
    if (!country) {
      return
    }

    const normalizedCountry = normalizeCountryName(country.name) ?? country.name
    const currentFields = collectedTravelRuleFieldsRef.current
    const targetCountryIsoCode = getCountryIsoCode(normalizedCountry)
    const normalizedRegionValue = currentFields.stateOrProvince?.trim() ?? ''
    let nextStateOrProvince = currentFields.stateOrProvince

    if (normalizedRegionValue.length > 0 && targetCountryIsoCode.length > 0) {
      const statesForCountry = State.getStatesOfCountry(targetCountryIsoCode)

      if (statesForCountry.length === 0) {
        nextStateOrProvince = ''
      } else {
        const normalizedRegionKey = countryLookupKey(normalizedRegionValue)
        const isKnownRegion = statesForCountry.some(state => {
          return (
            countryLookupKey(state.name) === normalizedRegionKey ||
            countryLookupKey(state.isoCode) === normalizedRegionKey
          )
        })

        if (!isKnownRegion) {
          nextStateOrProvince = ''
        }
      }
    }

    updateTRFAndCache({
      ...currentFields,
      countryOfResidence: normalizedCountry,
      stateOrProvince: nextStateOrProvince
    })
    clearValidationErrors('countryOfResidence')
  }

  const updateTRFAndCache = (newFields: TravelRuleFields): void => {
    collectedTravelRuleFieldsRef.current = newFields
    setCollectedTravelRuleFields(newFields)
    Cookies.set(COOKIE_KEY, JSON.stringify(newFields), { expires: 7 })
  }

  const toggleIsBusiness = (): void => {
    const newIsBusinessState = !isBusiness
    setIsBusiness(newIsBusinessState)

    const newFields = {
      ...collectedTravelRuleFields,
      companyName: newIsBusinessState ? collectedTravelRuleFields.companyName : '',
      accountType: newIsBusinessState ? CheckoutType.Business : CheckoutType.Individual
    }

    updateTRFAndCache(newFields)
    clearValidationErrors('companyName')
  }

  const debouncedFetch = useRef(
    _.debounce(
      (value: string, countryCode: string, setAddressSuggestionList) => {
        Radom.getGoolgePlaceAutoComplete(value, countryCode).then((res) => {
          setAddressSuggestionList(res)
        })
      },
      500
    )
  ).current

  const applyAddressFromSuggestion = (
    selectedAddress: string,
    details: AddressSuggestionDetails | null
  ): void => {
    const fallback = parseFallbackAddress(selectedAddress)
    const currentFields = collectedTravelRuleFieldsRef.current
    const normalizedCountry = normalizeCountryName(
      firstNonEmpty(
        details?.country,
        details?.countryCode,
        fallback.countryOfResidence,
        currentFields.countryOfResidence
      )
    ) ?? currentFields.countryOfResidence

    updateTRFAndCache({
      ...currentFields,
      addressLine1:
        firstNonEmpty(details?.addressLine1, fallback.addressLine1, currentFields.addressLine1) ?? '',
      addressLine2: firstNonEmpty(details?.addressLine2, currentFields.addressLine2) ?? '',
      townOrCity: firstNonEmpty(details?.townOrCity, fallback.townOrCity, currentFields.townOrCity) ?? '',
      stateOrProvince:
        firstNonEmpty(details?.stateOrProvince, fallback.stateOrProvince, currentFields.stateOrProvince) ?? '',
      postalCode: firstNonEmpty(details?.postalCode, fallback.postalCode, currentFields.postalCode) ?? '',
      countryOfResidence: normalizedCountry
    })
    clearValidationErrors('address', 'addressLine1', 'townOrCity', 'countryOfResidence')
  }

  const handleClickAddressSuggestion = async (address: string): Promise<void> => {
    setInputAddress(address)
    setAddressSuggestionList([])

    try {
      const countryIsoCode = getCountryIsoCode(collectedTravelRuleFieldsRef.current.countryOfResidence)
      const details = await Radom.getGooglePlaceAddressDetails(address, countryIsoCode)
      applyAddressFromSuggestion(address, details)
    } catch (err) {
      console.error('Error resolving selected address details:', err)
      applyAddressFromSuggestion(address, null)
    }
  }

  const enterManualAddressMode = (): void => {
    if (pendingAddressMode) {
      return
    }

    setPendingAddressMode('manual')
    if (addressModeTimeoutRef.current) {
      clearTimeout(addressModeTimeoutRef.current)
    }

    addressModeTimeoutRef.current = setTimeout(() => {
      const latestFields = collectedTravelRuleFieldsRef.current
      const fallback = parseFallbackAddress(inputAddress)

      if (!hasTextValue(latestFields.addressLine1) && hasTextValue(fallback.addressLine1)) {
        updateTRFAndCache({
          ...latestFields,
          addressLine1: fallback.addressLine1 ?? '',
          addressLine2: firstNonEmpty(latestFields.addressLine2, fallback.addressLine2) ?? '',
          townOrCity: firstNonEmpty(latestFields.townOrCity, fallback.townOrCity) ?? '',
          stateOrProvince: firstNonEmpty(latestFields.stateOrProvince, fallback.stateOrProvince) ?? '',
          postalCode: firstNonEmpty(latestFields.postalCode, fallback.postalCode) ?? ''
        })
      }

      setAddressSuggestionList([])
      setInputAddress('')
      setIsManualAddressEntry(true)
      setPendingAddressMode(null)
      addressModeTimeoutRef.current = null
    }, ADDRESS_MODE_DELAY_MS)
  }

  const enterAutocompleteAddressMode = (): void => {
    if (pendingAddressMode) {
      return
    }

    setPendingAddressMode('autocomplete')
    if (addressModeTimeoutRef.current) {
      clearTimeout(addressModeTimeoutRef.current)
    }

    addressModeTimeoutRef.current = setTimeout(() => {
      const latestFields = collectedTravelRuleFieldsRef.current
      const recomposedAddress = [
        latestFields.addressLine1,
        latestFields.addressLine2,
        latestFields.townOrCity,
        latestFields.stateOrProvince,
        latestFields.postalCode
      ]
        .map(value => value?.trim())
        .filter(value => value && value.length > 0)
        .join(', ')

      setInputAddress(recomposedAddress)
      setAddressSuggestionList([])
      setIsManualAddressEntry(false)
      setPendingAddressMode(null)
      addressModeTimeoutRef.current = null
    }, ADDRESS_MODE_DELAY_MS)
  }

  const handleContinue = async (): Promise<void> => {
    const latestFields = { ...collectedTravelRuleFieldsRef.current }
    const addressInputValue = inputAddress.trim()
    const fallbackAddress = parseFallbackAddress(addressInputValue)

    latestFields.accountType = isBusiness
      ? CheckoutType.Business
      : CheckoutType.Individual

    if (!isManualAddressEntry) {
      latestFields.addressLine1 =
        firstNonEmpty(fallbackAddress.addressLine1, addressInputValue) ?? latestFields.addressLine1
      latestFields.addressLine2 =
        firstNonEmpty(fallbackAddress.addressLine2, latestFields.addressLine2) ?? ''
      latestFields.townOrCity =
        firstNonEmpty(fallbackAddress.townOrCity, latestFields.townOrCity) ?? ''
      latestFields.stateOrProvince =
        firstNonEmpty(fallbackAddress.stateOrProvince, latestFields.stateOrProvince) ?? ''
      latestFields.postalCode =
        firstNonEmpty(fallbackAddress.postalCode, latestFields.postalCode) ?? ''
    }
    latestFields.countryOfResidence =
      normalizeCountryName(
        firstNonEmpty(
          latestFields.countryOfResidence,
          fallbackAddress.countryOfResidence
        )
      ) ?? ''

    const nextValidationErrors: ValidationErrors = {}
    if (isBusiness && !hasTextValue(latestFields.companyName)) {
      nextValidationErrors.companyName = 'Enter company name.'
    }

    if (!hasTextValue(latestFields.name)) {
      nextValidationErrors.name = 'Enter full name.'
    }

    if (!hasTextValue(latestFields.emailAddress)) {
      nextValidationErrors.emailAddress = 'Enter email address.'
    } else if (!simpleEmailValidation(latestFields.emailAddress)) {
      nextValidationErrors.emailAddress = 'Enter a valid email address.'
    }

    if (!hasTextValue(latestFields.addressLine1)) {
      if (isManualAddressEntry) {
        nextValidationErrors.addressLine1 = 'Enter address line 1.'
      } else {
        nextValidationErrors.address = 'Enter billing address.'
      }
    }

    if (!hasTextValue(latestFields.townOrCity)) {
      if (isManualAddressEntry) {
        nextValidationErrors.townOrCity = 'Enter city or town.'
      } else {
        nextValidationErrors.address =
          nextValidationErrors.address ?? 'Enter a full billing address including city or town.'
      }
    }

    if (!hasTextValue(latestFields.countryOfResidence)) {
      nextValidationErrors.countryOfResidence = 'Select country.'
    }

    const firstValidationField = Object.keys(nextValidationErrors)[0] as ValidationField | undefined
    setValidationErrors(nextValidationErrors)

    if (firstValidationField) {
      focusValidationField(firstValidationField)
      return
    }

    setErrorMessage('')
    updateTRFAndCache(latestFields)
    setIsContinuing(true)

    try {
      await new Promise(resolve => setTimeout(resolve, 120))
      await Promise.resolve(props.onTravelRuleFieldsComplete(latestFields))
    } catch (err: any) {
      setErrorMessage(err?.message ?? 'Failed to continue. Please try again.')
      setIsContinuing(false)
    }
  }

  useEffect(() => {
    if (!props.prefilledData) {
      return
    }

    const nextFields = { ...collectedTravelRuleFieldsRef.current }
    let didChange = false

    if (!hasTextValue(nextFields.name) && hasTextValue(props.prefilledData.name)) {
      nextFields.name = props.prefilledData.name as string
      didChange = true
    }

    if (!hasTextValue(nextFields.emailAddress) && hasTextValue(props.prefilledData.email)) {
      nextFields.emailAddress = props.prefilledData.email as string
      didChange = true
    }

    if (!hasTextValue(nextFields.companyName) && hasTextValue(props.prefilledData.companyName)) {
      nextFields.companyName = props.prefilledData.companyName as string
      didChange = true
    }

    const normalizedResidenceCountry = normalizeCountryName(props.prefilledData.residenceCountry)
    if (!hasTextValue(nextFields.countryOfResidence) && hasTextValue(normalizedResidenceCountry)) {
      nextFields.countryOfResidence = normalizedResidenceCountry as string
      didChange = true
    }

    const parsedPrefilledAddress = parseFallbackAddress(props.prefilledData.billingAddress ?? '')
    if (!hasTextValue(nextFields.addressLine1) && hasTextValue(parsedPrefilledAddress.addressLine1)) {
      nextFields.addressLine1 = parsedPrefilledAddress.addressLine1 as string
      didChange = true
    }
    if (!hasTextValue(nextFields.addressLine2) && hasTextValue(parsedPrefilledAddress.addressLine2)) {
      nextFields.addressLine2 = parsedPrefilledAddress.addressLine2 as string
      didChange = true
    }
    if (!hasTextValue(nextFields.townOrCity) && hasTextValue(parsedPrefilledAddress.townOrCity)) {
      nextFields.townOrCity = parsedPrefilledAddress.townOrCity as string
      didChange = true
    }
    if (!hasTextValue(nextFields.stateOrProvince) && hasTextValue(parsedPrefilledAddress.stateOrProvince)) {
      nextFields.stateOrProvince = parsedPrefilledAddress.stateOrProvince as string
      didChange = true
    }
    if (!hasTextValue(nextFields.postalCode) && hasTextValue(parsedPrefilledAddress.postalCode)) {
      nextFields.postalCode = parsedPrefilledAddress.postalCode as string
      didChange = true
    }
    if (!hasTextValue(nextFields.countryOfResidence) && hasTextValue(parsedPrefilledAddress.countryOfResidence)) {
      nextFields.countryOfResidence = parsedPrefilledAddress.countryOfResidence as string
      didChange = true
    }

    if (didChange) {
      updateTRFAndCache(nextFields)
    }
  }, [
    props.prefilledData?.billingAddress,
    props.prefilledData?.companyName,
    props.prefilledData?.email,
    props.prefilledData?.name,
    props.prefilledData?.residenceCountry
  ])

  useEffect(() => {
    if (hasAttemptedCountryPrefillRef.current) {
      return
    }

    if (hasTextValue(collectedTravelRuleFieldsRef.current.countryOfResidence)) {
      hasAttemptedCountryPrefillRef.current = true
      return
    }

    hasAttemptedCountryPrefillRef.current = true
    let isActive = true

    void Radom.getCountryGuess().then((countryGuess) => {
      if (!isActive || !countryGuess) {
        return
      }

      if (hasTextValue(collectedTravelRuleFieldsRef.current.countryOfResidence)) {
        return
      }

      const guessedCountry =
        normalizeCountryName(countryGuess.countryName) ??
        normalizeCountryName(countryGuess.countryCode)

      if (!hasTextValue(guessedCountry)) {
        return
      }

      const matchedCountry =
        COUNTRIES.find(country => country.name === guessedCountry) ??
        COUNTRIES.find(country => country.isoCode === countryGuess.countryCode?.toUpperCase())

      if (!matchedCountry) {
        return
      }

      const matchedCountrySelection: { name: string } = { name: matchedCountry.name }
      onCountrySelected(matchedCountrySelection)
    })

    return () => {
      isActive = false
    }
  }, [])

  useEffect(() => {
    if (hasTextValue(inputAddress)) {
      return
    }

    const fallbackInputAddress = [
      collectedTravelRuleFields.addressLine1,
      collectedTravelRuleFields.townOrCity,
      collectedTravelRuleFields.stateOrProvince,
      collectedTravelRuleFields.postalCode
    ]
      .map(value => value?.trim())
      .filter(value => value && value.length > 0)
      .join(', ')

    if (fallbackInputAddress.length > 0) {
      setInputAddress(fallbackInputAddress)
    }
  }, [
    collectedTravelRuleFields.addressLine1,
    collectedTravelRuleFields.postalCode,
    collectedTravelRuleFields.stateOrProvince,
    collectedTravelRuleFields.townOrCity,
    inputAddress
  ])

  useEffect(() => {
    return () => {
      if (addressModeTimeoutRef.current) {
        clearTimeout(addressModeTimeoutRef.current)
      }
    }
  }, [])

  return (
    <TravelRuleFieldsContainer>
      {errorMessage && errorMessage.length > 0 && (
        <div
          style={{
            backgroundColor: RADOM_COLORS.ERROR,
            padding: 15,
            marginBottom: 10,
            borderRadius: 5,
            fontSize: 14,
            color: 'white',
            overflowWrap: 'break-word'
          }}
        >
          {errorMessage}
        </div>
      )}

      <EmailSection>
        <TravelRuleFieldsRow key="missingEmailAddress">
          {generateInputLabel('email', isBusiness)}
          <TextInput
            style={fieldErrorStyle('emailAddress', FIELD_INPUT_STYLE)}
            key="trEmailAddress"
            name="email"
            autoComplete="email"
            value={collectedTravelRuleFields.emailAddress}
            placeholder={'Email address'}
            required={true}
            aria-invalid={Boolean(validationErrors.emailAddress)}
            data-validation-field="emailAddress"
            onInput={(el) => {
              clearValidationErrors('emailAddress')
              updateTRFAndCache({
                ...collectedTravelRuleFields,
                emailAddress: el.currentTarget.value.toLowerCase()
              })
            }}
          />
          {validationErrors.emailAddress && <InlineInputError>{validationErrors.emailAddress}</InlineInputError>}
        </TravelRuleFieldsRow>
      </EmailSection>

      <BusinessToggleRow
        role='switch'
        aria-checked={isBusiness}
        tabIndex={0}
        onClick={toggleIsBusiness}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggleIsBusiness()
          }
        }}
      >
        <div onClick={(e) => { e.stopPropagation() }}>
          <Switch
            checked={isBusiness}
            onClick={toggleIsBusiness}
          />
        </div>
        <div
          style={{
            flex: 1,
            color: '#2d3648',
            fontSize: 14,
            overflowWrap: 'break-word',
            marginLeft: 8
          }}
        >
          Paying as a business?
        </div>
      </BusinessToggleRow>

      {isBusiness && (
        <IdentitySection>
          <TravelRuleFieldsRow key="missingCompanyName">
            {generateInputLabel('company', true)}
            <TextInput
              style={fieldErrorStyle('companyName', FIELD_INPUT_STYLE)}
              key="trCompanyName"
              name="organization"
              autoComplete="organization"
              value={collectedTravelRuleFields.companyName}
              placeholder={'Company name'}
              required={true}
              aria-invalid={Boolean(validationErrors.companyName)}
              data-validation-field="companyName"
              onInput={(el) => {
                clearValidationErrors('companyName')
                updateTRFAndCache({
                  ...collectedTravelRuleFields,
                  companyName: el.currentTarget.value
                })
              }}
            />
            {validationErrors.companyName && <InlineInputError>{validationErrors.companyName}</InlineInputError>}
          </TravelRuleFieldsRow>
        </IdentitySection>
      )}

      <BillingSection>
        <BillingSectionTitle>Billing address</BillingSectionTitle>
        <TravelRuleFieldsRow key="missingName">
          {generateInputLabel('name', isBusiness)}
          <TextInput
            style={fieldErrorStyle('name', FIELD_INPUT_STYLE)}
            key="trName"
            name="name"
            autoComplete="name"
            value={collectedTravelRuleFields.name}
            placeholder={'Full name'}
            required={true}
            aria-invalid={Boolean(validationErrors.name)}
            data-validation-field="name"
            onInput={(el) => {
              clearValidationErrors('name')
              updateTRFAndCache({
                ...collectedTravelRuleFields,
                name: el.currentTarget.value
              })
            }}
          />
          {validationErrors.name && <InlineInputError>{validationErrors.name}</InlineInputError>}
        </TravelRuleFieldsRow>
        {shouldCollectCountry && (
          <>
            {generateInputLabel('country', isBusiness)}
            <TravelRuleFieldsRow
              key="countryOfResidence"
              data-validation-field="countryOfResidence"
            >
              <CountrySelector
                style={fieldErrorStyle('countryOfResidence', COUNTRY_SELECTOR_STYLE)}
                selectedCountry={selectedCountryName}
                onCountrySelected={onCountrySelected}
              />
              {validationErrors.countryOfResidence && (
                <InlineInputError>{validationErrors.countryOfResidence}</InlineInputError>
              )}
            </TravelRuleFieldsRow>
          </>
        )}

        {shouldCollectAddress && (
          <>
            <AddressFieldsGroup visible={!isManualAddressEntry} expandedHeight={140}>
              {generateInputLabel('billingAddress', isBusiness)}
              <div data-validation-field="address">
                <TextInputWithLabelWithSuggestions
                  label=""
                  inputStyle={FIELD_INPUT_STYLE}
                  isInvalid={Boolean(validationErrors.address)}
                  validationError={validationErrors.address}
                  placeholder="Address"
                  value={inputAddress ?? ''}
                  required={true}
                  suggestionList={addressSuggestionList}
                  setSelectedItems={(items) => {
                    if (items[0]) {
                      void handleClickAddressSuggestion(items[0])
                    }
                  }}
                  onChange={(e) => {
                    clearValidationErrors('address', 'addressLine1', 'townOrCity')
                    const value = (e.target as HTMLInputElement).value
                    const trimmed = value.trim()
                    if (value.length > 2) {
                      const countryIsoCode = getCountryIsoCode(collectedTravelRuleFields.countryOfResidence)
                      debouncedFetch(
                        value,
                        countryIsoCode,
                        setAddressSuggestionList
                      )
                    } else {
                      setAddressSuggestionList([])
                    }

                    const fallback = parseFallbackAddress(value)
                    updateTRFAndCache({
                      ...collectedTravelRuleFieldsRef.current,
                      addressLine1: firstNonEmpty(fallback.addressLine1, trimmed) ?? '',
                      addressLine2: fallback.addressLine2 ?? '',
                      townOrCity: fallback.townOrCity ?? '',
                      stateOrProvince: fallback.stateOrProvince ?? '',
                      postalCode: fallback.postalCode ?? ''
                    })
                    setInputAddress(value)
                  }}
                />
              </div>
              <AddressModeRow>
                <AddressManualLink
                  type="button"
                  disabled={pendingAddressMode !== null}
                  onMouseDown={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    enterManualAddressMode()
                  }}
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                >
                    Enter address manually
                </AddressManualLink>
              </AddressModeRow>
            </AddressFieldsGroup>

            <AddressFieldsGroup visible={isManualAddressEntry} expandedHeight={320}>
              <TextInput
                style={fieldErrorStyle('addressLine1', FIELD_INPUT_STYLE)}
                name="address-line1"
                autoComplete="address-line1"
                value={collectedTravelRuleFields.addressLine1}
                placeholder={'Address line 1'}
                required={true}
                aria-invalid={Boolean(validationErrors.addressLine1)}
                data-validation-field="addressLine1"
                onInput={(el) => {
                  clearValidationErrors('addressLine1', 'address')
                  updateTRFAndCache({
                    ...collectedTravelRuleFields,
                    addressLine1: el.currentTarget.value
                  })
                }}
              />
              {validationErrors.addressLine1 && <InlineInputError>{validationErrors.addressLine1}</InlineInputError>}
              <AddressModeRow>
                <AddressManualLink
                  type="button"
                  disabled={pendingAddressMode !== null}
                  onMouseDown={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    enterAutocompleteAddressMode()
                  }}
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                >
                    Use address autocomplete
                </AddressManualLink>
              </AddressModeRow>
              <TextInput
                style={FIELD_INPUT_STYLE}
                name="address-line2"
                autoComplete="address-line2"
                value={collectedTravelRuleFields.addressLine2}
                placeholder={'Address line 2'}
                onInput={(el) => {
                  updateTRFAndCache({
                    ...collectedTravelRuleFields,
                    addressLine2: el.currentTarget.value
                  })
                }}
              />
              <FieldPair>
                <div>
                  <TextInput
                    style={fieldErrorStyle('townOrCity', FIELD_INPUT_STYLE)}
                    name="address-level2"
                    autoComplete="address-level2"
                    value={collectedTravelRuleFields.townOrCity}
                    placeholder={'City / Town'}
                    required={true}
                    aria-invalid={Boolean(validationErrors.townOrCity)}
                    data-validation-field="townOrCity"
                    onInput={(el) => {
                      clearValidationErrors('townOrCity', 'address')
                      updateTRFAndCache({
                        ...collectedTravelRuleFields,
                        townOrCity: el.currentTarget.value
                      })
                    }}
                  />
                  {validationErrors.townOrCity && <InlineInputError>{validationErrors.townOrCity}</InlineInputError>}
                </div>
                <TextInput
                  style={FIELD_INPUT_STYLE}
                  name="postal-code"
                  autoComplete="postal-code"
                  value={collectedTravelRuleFields.postalCode}
                  placeholder={'Postcode / ZIP'}
                  onInput={(el) => {
                    updateTRFAndCache({
                      ...collectedTravelRuleFields,
                      postalCode: el.currentTarget.value
                    })
                  }}
                />
              </FieldPair>
              {shouldShowStateOrRegionField && (
                <TextInput
                  style={FIELD_INPUT_STYLE}
                  name="address-level1"
                  autoComplete="address-level1"
                  value={collectedTravelRuleFields.stateOrProvince}
                  placeholder={'State / Region'}
                  onInput={(el) => {
                    updateTRFAndCache({
                      ...collectedTravelRuleFields,
                      stateOrProvince: el.currentTarget.value
                    })
                  }}
                />
              )}
            </AddressFieldsGroup>
          </>
        )}
      </BillingSection>

      <ContinueActionSection extraBottomSpace={isBusiness && isManualAddressEntry}>
        <PrimaryButton
          isLoading={isContinuing}
          style={{
            background: '#3f6bf5',
            width: '100%',
            marginTop: 0,
            fontWeight: 410,
            minHeight: 48,
            borderRadius: 7,
            padding: '12px 14px'
          }}
          onClick={() => { void handleContinue() }}
        >
          Continue
        </PrimaryButton>
        <span
          style={{
            width: '100%',
            textAlign: 'center',
            fontSize: 11,
            color: '#98a4b7'
          }}
        >
          You&apos;ll confirm the crypto transfer in the next step.
        </span>
      </ContinueActionSection>
    </TravelRuleFieldsContainer>
  )
}

export default TravelRuleFieldsWidget
